<!--
  PagamentoLink — página minimalista de cobrança via link.

  Objetivo: foco absoluto em converter o clique em pagamento.
  Sem header, sem banner, sem switcher — toda a personalização
  acontece via query string do link enviado ao cliente.

  Query params suportados:
    ?valor=150.00    — valor em euros (fallback: 300.00)
    ?label=Texto...  — sobrescreve o texto acima do valor (opcional)
    ?es | ?en | ?br | ?pt — idioma (fallback: es)
    ?az              — botão azul (sem a flag, o padrão é laranja)
-->
<template>
  <div class="pag-page">
    <Transition name="pag-fade" appear>
      <main
        class="pag-card"
        role="main"
        :aria-label="t.ariaCard"
      >
        <header class="pag-logo">
          <img :src="logoBranco" alt="OligoCheck" width="160" height="32" />
        </header>

        <p class="pag-label">{{ t.label }}</p>

        <h1 class="pag-valor" :aria-label="`${valorFormatado} ${t.moeda}`">
          <span class="pag-valor-moeda" aria-hidden="true">€</span>{{ valorFormatado }}
        </h1>

        <button
          type="button"
          :class="[
            'pag-botao',
            `pag-botao--${corBotao}`,
            { 'pag-botao--carregando': isNavigating }
          ]"
          :disabled="isNavigating"
          @click="realizarPagamento"
        >
          <template v-if="!isNavigating">
            <span>{{ t.botao }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </template>
          <template v-else>
            <span class="pag-spinner" aria-hidden="true"></span>
            <span>{{ t.processando }}</span>
          </template>
        </button>

        <footer class="pag-rodape">
          <span class="pag-selo">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>{{ t.seguranca }}</span>
          </span>
          <span class="pag-separador" aria-hidden="true">·</span>
          <span class="pag-paypal">PayPal</span>
        </footer>
      </main>
    </Transition>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import logoBranco from '@/assets/images/logo-branco.svg'

const route = useRoute()
const isNavigating = ref(false)

/* ============================================================
   Destino e valor padrão
   ============================================================ */
const URL_PAYPAL = 'https://www.paypal.com/ncp/payment/K2EN5RUEK8JNE'
const VALOR_PADRAO = 300.0

/* ============================================================
   i18n
   ============================================================ */
const traducoes = {
  es: {
    label: 'Reserva Prioritaria OligoCheck',
    botao: 'Pagar ahora',
    processando: 'Procesando…',
    seguranca: 'Pago seguro',
    moeda: 'euros',
    ariaCard: 'Formulario de pago OligoCheck'
  },
  pt: {
    label: 'Reserva Prioritária OligoCheck',
    botao: 'Pagar agora',
    processando: 'Processando…',
    seguranca: 'Pagamento seguro',
    moeda: 'euros',
    ariaCard: 'Formulário de pagamento OligoCheck'
  },
  en: {
    label: 'OligoCheck Priority Reservation',
    botao: 'Pay now',
    processando: 'Processing…',
    seguranca: 'Secure payment',
    moeda: 'euros',
    ariaCard: 'OligoCheck payment form'
  }
}

/* ============================================================
   Query params
   ============================================================ */
const idiomaAtual = computed(() => {
  const q = route.query
  if ('es' in q) return 'es'
  if ('en' in q) return 'en'
  if ('br' in q || 'pt' in q) return 'pt'
  return 'es'
})

const corBotao = computed(() => ('az' in route.query ? 'azul' : 'laranja'))

const valor = computed(() => {
  const v = parseFloat(route.query.valor)
  return Number.isFinite(v) && v > 0 ? v : VALOR_PADRAO
})

const valorFormatado = computed(() =>
  valor.value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
)

const t = computed(() => {
  const base = traducoes[idiomaAtual.value] || traducoes.es
  const labelOverride =
    typeof route.query.label === 'string' ? route.query.label : null
  return labelOverride ? { ...base, label: labelOverride } : base
})

/* ============================================================
   Ação — redireciona para PayPal
   ============================================================ */
const realizarPagamento = () => {
  if (isNavigating.value) return
  isNavigating.value = true
  /* delay pequeno só para o usuário ver o feedback visual de “procesando”
     antes do redirect (evita sensação de clique morto).                 */
  setTimeout(() => {
    window.location.href = URL_PAYPAL
  }, 900)
}

/* Sem useSeo: meta/título ficam neutros; /pago já está em Disallow no robots.txt */
</script>

<style scoped>
/* ============================================================
   Layout da tela — centralização e fundo sutil
   ============================================================ */
.pag-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ds-s-5);
  background-color: var(--ds-bg);
  background-image:
    radial-gradient(ellipse 900px 600px at 50% -10%, rgba(20, 203, 240, 0.1), transparent 60%),
    radial-gradient(ellipse 700px 600px at 50% 110%, rgba(245, 130, 32, 0.05), transparent 60%);
  font-family: var(--ds-font-body);
}

/* ============================================================
   Card único — foco total no valor + botão
   ============================================================ */
.pag-card {
  width: 100%;
  max-width: 420px;
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-2xl);
  padding: var(--ds-s-8) var(--ds-s-7);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: var(--ds-shadow-lg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

/* Logo */
.pag-logo {
  margin-bottom: var(--ds-s-7);
}
.pag-logo img {
  height: 32px;
  width: auto;
  display: block;
  opacity: 0.95;
}

/* Label acima do valor */
.pag-label {
  font-family: var(--ds-font-bold);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--ds-text-dim);
  margin: 0 0 var(--ds-s-3);
  max-width: 100%;
  overflow-wrap: anywhere;
}

/* Valor — elemento principal da tela */
.pag-valor {
  font-family: var(--ds-font-display);
  font-size: clamp(44px, 11vw, 60px);
  line-height: 1;
  color: var(--ds-text);
  margin: 0;
  letter-spacing: -0.5px;
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}
.pag-valor-moeda {
  font-size: 0.42em;
  margin-top: 0.4em;
  color: var(--ds-text-dim);
  font-family: var(--ds-font-body);
}

/* ============================================================
   Botão de pagamento
   ============================================================ */
.pag-botao {
  margin-top: var(--ds-s-7);
  width: 100%;
  padding: 17px 22px;
  border-radius: var(--ds-r-lg);
  border: 0;
  cursor: pointer;
  font-family: var(--ds-font-bold);
  font-size: 16px;
  letter-spacing: 0.2px;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  transition:
    transform var(--ds-t-base),
    box-shadow var(--ds-t-base),
    background var(--ds-t-base),
    filter var(--ds-t-base);
}

.pag-botao svg { transition: transform var(--ds-t-base); }
.pag-botao:hover:not(:disabled) svg { transform: translateX(3px); }
.pag-botao:active:not(:disabled) { transform: scale(0.985); }
.pag-botao:focus-visible {
  outline: 3px solid currentColor;
  outline-offset: 3px;
}
.pag-botao:disabled { cursor: progress; opacity: 0.9; }

/* Variante laranja (padrão) */
.pag-botao--laranja {
  background: var(--ds-warning);
  box-shadow: 0 10px 26px -8px rgba(245, 130, 32, 0.55);
  animation: pag-pulse-lar 2.4s ease-in-out infinite;
}
.pag-botao--laranja:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-2px);
  box-shadow: 0 14px 32px -8px rgba(245, 130, 32, 0.7);
  animation-play-state: paused;
}

/* Variante azul (?az) */
.pag-botao--azul {
  background: var(--ds-accent);
  color: #06121a;
  box-shadow: 0 10px 26px -8px rgba(20, 203, 240, 0.5);
  animation: pag-pulse-az 2.4s ease-in-out infinite;
}
.pag-botao--azul:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-2px);
  box-shadow: 0 14px 32px -8px rgba(20, 203, 240, 0.7);
  animation-play-state: paused;
}

.pag-botao--carregando { animation: none !important; }

/* Spinner dentro do botão */
.pag-spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2.5px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: pag-spin 0.8s linear infinite;
  display: inline-block;
}
.pag-botao--azul .pag-spinner {
  border-color: rgba(6, 18, 26, 0.3);
  border-top-color: #06121a;
}

/* ============================================================
   Rodapé do card — selo de segurança + PayPal
   ============================================================ */
.pag-rodape {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: var(--ds-s-6);
  color: var(--ds-text-faint);
  font-size: 12px;
}
.pag-selo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.pag-separador { opacity: 0.4; }
.pag-paypal {
  font-family: var(--ds-font-bold);
  color: var(--ds-text-dim);
  letter-spacing: 0.3px;
}

/* ============================================================
   Animações
   ============================================================ */
@keyframes pag-spin {
  to { transform: rotate(360deg); }
}
@keyframes pag-pulse-lar {
  0%, 100% { box-shadow: 0 10px 26px -8px rgba(245, 130, 32, 0.55); }
  50%      { box-shadow: 0 14px 32px -6px rgba(245, 130, 32, 0.75); }
}
@keyframes pag-pulse-az {
  0%, 100% { box-shadow: 0 10px 26px -8px rgba(20, 203, 240, 0.5); }
  50%      { box-shadow: 0 14px 32px -6px rgba(20, 203, 240, 0.7); }
}

/* Entrada suave do card */
.pag-fade-enter-active {
  transition: opacity 0.55s cubic-bezier(0.2, 0.8, 0.2, 1),
              transform 0.55s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.pag-fade-enter-from { opacity: 0; transform: translateY(18px); }
.pag-fade-enter-to   { opacity: 1; transform: translateY(0); }

/* ============================================================
   Responsivo
   ============================================================ */
@media (max-width: 480px) {
  .pag-page {
    padding: 0;
    align-items: stretch;
  }
  .pag-card {
    min-height: 100dvh;
    max-width: 100%;
    border-radius: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
    padding: 80px 24px 40px;
    justify-content: center;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pag-botao { animation: none !important; }
  .pag-fade-enter-active { transition: none; }
}
</style>
